import { useCallback, useEffect, useMemo, useState } from "react";
import { subscribeToPresenceUpdates } from "../services/Wamp";
import useWamp from "./useWamp";
import { useFocusEffect } from "@react-navigation/native";

export default function usePresence(username) {
    const wamp = useWamp();
    const [userId, setUserId] = useState(null);
    const [status, setStatus] = useState("offline");
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const abortCtrl = useMemo(() => new AbortController(), []);

    const checkAndUpdatePresence = () => {
        getCachedPresence(wamp, username).then((presence) => {
            if (abortCtrl.signal.aborted) {
                return;
            }

            setStatus(presence.status);
            setUserId(presence.id);
            setInitialFetchDone(true);
        }, (err) => {
            console.error("error getting presence (manual)", err);
            setStatus("offline");
        });
    };

    useFocusEffect(useCallback(() => {
        return () => abortCtrl.abort();
    }, []));

    useFocusEffect(useCallback(() => {
        if (!wamp) {
            return () => {};
        }

        checkAndUpdatePresence();
    }, [abortCtrl, wamp, username]));

    useFocusEffect(useCallback(() => {
        if (!initialFetchDone) {
            return () => {};
        };

        let intermittentCheckTimeout = null;
        const rescheduleCheck = () => {
            if (intermittentCheckTimeout) {
                clearTimeout(intermittentCheckTimeout);
                intermittentCheckTimeout = null;
            }

            setTimeout(() => {
                intermittentCheckTimeout = null;
                checkAndUpdatePresence();
            }, 15000 + Math.floor(Math.random() * 5000));
        };
        rescheduleCheck();


        const unsub = subscribeToPresenceUpdates(userId, ([presence]) => {
            setStatus(presence.status);
            rescheduleCheck();
        });

        return () => unsub();
    }, [abortCtrl, initialFetchDone, wamp, username]));

    return status;
}

const lastPresenceCheck = new Map();

export function getCachedPresence(wamp, username) {
    if (lastPresenceCheck.has(username)) {
        const { lastCheckAt, lastPresence, promise } = lastPresenceCheck.get(username);
        if (!promise && lastCheckAt >= Date.now() - 10000) {
            return Promise.resolve(lastPresence);
        } else if (promise) {
            return promise;
        }
    }

    const promise = wamp.getPresence(username).then((presence) => {
        lastPresenceCheck.set(username, {lastCheckAt: Date.now(), lastPresence: presence, promise: null});
        return presence;
    });
    lastPresenceCheck.set(username, {lastCheckAt: Date.now(), lastPresence: null, promise});

    return promise;
}